import SaveIcon from "@mui/icons-material/Save";
import LoadingButton from "@mui/lab/LoadingButton";
import { Alert, Grid, LinearProgress, Stack } from "@mui/material";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import { useToastMessage } from "@poseidon/ui/app/toasts/useToastMessage";
import {
  FormikDisabler,
  FullWidthField,
  useAuth,
  usePermissions,
} from "@poseidon/ui/components";
import ErrorBoundary from "@poseidon/ui/components/ErrorBoundary";
import { Field, Form, Formik } from "formik";
import React, { useMemo, useState } from "react";
import { AutoComplete } from "@poseidon/ui/components/AutoComplete";
import { useAction } from "./useAction";
import config from "@poseidon/utils/config";
import * as api from "../Applications/api";
import { ActionChangeLogTable } from "./ActionChangeLogTable";
import { useCollectionData } from "react-firebase-hooks/firestore";
import { collection } from "firebase/firestore";
import { getDate } from "@poseidon/firebase/timestamp";
import * as Yup from "yup";

export const ActionSchema = Yup.object().shape({
  note: Yup.string().max(200).default(""),
  combined: Yup.string().required("Action state is required"),
});

function getActionForDropdown(entity, entityId, action, application) {
  let actionForOptions = [
    {
      label: "Salt Finance",
      value: "admin",
    },
  ];

  if (entity === config.SaltCollection.Sailor) {
    return [
      ...actionForOptions,
      {
        label: "Sailor",
        value: `sailors/${entityId}`,
      },
    ];
  }
  if (entity === config.collections.application) {
    if (!action.origin) {
      console.error("Origin not defined for action, however it should exist");
    }
    if (action.origin === config.collections.dealer) {
      return [
        ...actionForOptions,
        {
          label: "Dealer",
          value: `dealers/${application.dealer?.id}`,
        },
      ];
    }

    if (action.origin === config.SaltCollection.Sailor) {
      return [
        ...actionForOptions,
        {
          label: "Sailor",
          value: `sailors/${application.applicant?.sailor?.id}`,
        },
      ];
    }
    //todo: this is for loanDocs so it can be assigned to sailor - maybe a better way (defining what actions can be assigned to non origin users)
    if (action.origin === "admin") {
      return [
        ...actionForOptions,
        // {
        //   label: "Dealer",
        //   value: `dealers/${application.dealer?.id}`,
        // },
        {
          label: "Sailor",
          value: `sailors/${application.applicant?.sailor?.id}`,
        },
      ];
    }
    return actionForOptions;
  }
}
const actionsLogConverter = {
  fromFirestore(snapshot) {
    const data = snapshot.data();
    return {
      ...data,
      createdAt: getDate(data.createdAt),
    };
  },
  toFirestore: (data) => data,
};
const { actionStateOptions, collections } = config;
function Action({ entity, entityId, entityKey, application = null, readonly }) {
  const { canWrite } = usePermissions();
  const { setError } = useErrorMessage();
  const { setToast } = useToastMessage();
  const { action, loading, error, docRef } = useAction({
    entity,
    entityId,
    entityKey,
  });
  const [actionLog, loadingLog, errorLog] = useCollectionData(
    collection(docRef, "log").withConverter(actionsLogConverter)
  );
  const { user } = useAuth();

  const [loadingTimeout, setLoadingTimeout] = useState(false);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setLoadingTimeout(true);
    }, 3000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const actionFor =
    action?.actionFor === "admin" ? "admin" : action?.actionFor.path;

  const combinedOptions = useMemo(() => {
    if (!action) {
      return [];
    }
    const actionRequiredOption = actionStateOptions.find(
      (a) => a.value === "actionRequired"
    );
    const otherOptions = actionStateOptions.filter(
      (a) => a.value !== "actionRequired"
    );
    const actionForOptions = getActionForDropdown(
      entity,
      entityId,
      action,
      application
    );
    return [
      ...otherOptions.map(({ value, label }) => ({
        value: `${value}:admin`,
        label,
      })),
      ...actionForOptions.map(({ value, label }) => ({
        value: `${actionRequiredOption.value}:${value}`,
        label: `${actionRequiredOption.label}: ${label}`,
      })),
    ];
  }, [entity, entityId, action, application]);

  const onSubmitHandler = async ({ combined, note }) => {
    const [state, actionFor] = combined.split(":");
    const response = await api.setAction(
      entity,
      entityId,
      entityKey,
      {
        state,
        actionFor,
        note,
      },
      user.uid
    );

    if (response instanceof Error) {
      setError(response.message);
    } else {
      setToast("Action updated");
    }
  };

  if (entity === collections.application && !application) {
    setError(
      "Action component requires valid Application object if entity is set to applications"
    );
    return null;
  }

  if (error) {
    setError(error);
  }

  if (loading || !action) {
    return loadingTimeout ? (
      <Alert severity="error" sx={{ my: 2 }}>
        Failed to load Action status
      </Alert>
    ) : (
      <LinearProgress />
    );
  }

  return (
    <Stack mt={2} spacing={2}>
      <Formik
        initialValues={{
          ...action,
          actionFor: actionFor,
          combined: `${action.state}:${
            action.state === "actionRequired" ? actionFor : "admin"
          }`,
        }}
        validationSchema={ActionSchema}
        onSubmit={onSubmitHandler}
        enableReinitialize={true}
      >
        {({ values, isSubmitting, dirty, errors, touched }) => {
          return (
            <Form>
              <FormikDisabler disabled={readonly || !canWrite} />
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <AutoComplete
                    name="combined"
                    label="State"
                    options={combinedOptions}
                    error={!!(errors.combined && touched.combined)}
                  />
                </Grid>
                {values.combined.startsWith("actionRequired:") && (
                  <Grid item xs={12}>
                    <Field
                      component={FullWidthField}
                      name="note"
                      // size="small"
                      label="What action is required?"
                      margin="normal"
                      error={!!(errors.note && touched.note)}
                    />
                  </Grid>
                )}
                <Grid
                  item
                  xs={12}
                  sx={{ display: "flex", justifyContent: "right" }}
                >
                  <LoadingButton
                    type="submit"
                    loading={isSubmitting}
                    disabled={!dirty || readonly || !canWrite}
                    loadingPosition="start"
                    startIcon={<SaveIcon />}
                    variant="contained"
                    color="primary"
                    size="small"
                    margin="normal"
                  >
                    Update
                  </LoadingButton>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
      <ActionChangeLogTable
        log={actionLog}
        loading={loadingLog}
        error={errorLog}
      />
    </Stack>
  );
}

export default function ErrorCaughtAction(props) {
  return (
    <ErrorBoundary>
      <Action {...props} />
    </ErrorBoundary>
  );
}
